import {
	endOfDay,
	endOfMonth,
	format,
	isValid,
	startOfDay,
	startOfMonth,
	subDays,
	subMonths,
} from 'date-fns'
import { isString } from 'lodash-es'

// const format = (date, format) => formatTz(date, format, { timeZone: 'UTC' })

export const localToLocalUtc = (
	dateString: string | number | Date = Date.now(),
) => {
	const date = new Date(dateString)

	date.setMinutes(date.getMinutes() + date.getTimezoneOffset())

	return date
}

const utc = (dateString: string | number | Date = Date.now()) => {
	const date = new Date(dateString)

	return new Date(
		Date.UTC(
			date.getFullYear(),
			date.getMonth(),
			date.getDate(),
			date.getHours(),
			date.getMinutes(),
			date.getSeconds(),
		),
	)
}

const FORMAT = 'yyyy-MM-dd'

const RANGES: Record<
	string,
	{
		label: string
		from: (now: Date) => string
		to: (now: Date, includeToday?: boolean) => string
		allowIncludeToday?: boolean
		hidden?: boolean
	}
> = {
	today: {
		label: 'Today',
		from: (now) => format(startOfDay(now), FORMAT),
		to: (now) => format(endOfDay(now), FORMAT),
	},
	yesterday: {
		label: 'Yesterday',
		from: (now) => format(startOfDay(subDays(now, 1)), FORMAT),
		to: (now) => format(endOfDay(subDays(now, 1)), FORMAT),
	},
	past_7_days: {
		label: 'Past 7 Days',
		from: (now) => format(startOfDay(subDays(now, 7)), FORMAT),
		to: (now, includeToday) =>
			format(startOfDay(subDays(now, includeToday ? 0 : 1)), FORMAT),
		allowIncludeToday: true,
	},
	past_14_days: {
		label: 'Past 14 Days',
		from: (now) => format(startOfDay(subDays(now, 14)), FORMAT),
		to: (now, includeToday) =>
			format(startOfDay(subDays(now, includeToday ? 0 : 1)), FORMAT),
		allowIncludeToday: true,
	},
	past_30_days: {
		label: 'Past 30 Days',
		from: (now) => format(startOfDay(subDays(now, 30)), FORMAT),
		to: (now, includeToday) =>
			format(startOfDay(subDays(now, includeToday ? 0 : 1)), FORMAT),
		allowIncludeToday: true,
	},
	this_month: {
		label: 'This Month',
		from: (now) => format(startOfMonth(now), FORMAT),
		to: (now, includeToday) =>
			format(startOfDay(subDays(now, includeToday ? 0 : 1)), FORMAT),
		allowIncludeToday: true,
	},
	last_month: {
		label: 'Last Month',
		from: (now) => format(startOfMonth(subMonths(now, 1)), FORMAT),
		to: (now) => format(endOfMonth(subMonths(now, 1)), FORMAT),
	},
} as const

function getLabel(name) {
	return RANGES[name] ? RANGES[name].label : name
}

export type RangeKey = keyof typeof RANGES

function getRanges() {
	return Object.keys(RANGES)
		.filter((r) => typeof RANGES[r].hidden === 'undefined' || !RANGES[r].hidden)
		.map((id: RangeKey) => {
			return {
				id,
				label: RANGES[id].label,
				allowIncludeToday: RANGES[id]?.allowIncludeToday,
			}
		})
}

function getRange(name) {
	return RANGES[name]
}

function parseDate(dateRange, includeToday = false) {
	const now = new Date()

	if (RANGES[dateRange]) {
		const { from, to } = RANGES[dateRange]

		return {
			from: from(now),
			to: to(now, includeToday),
		}
	}

	if (isString(dateRange)) {
		const { 1: from, 2: to } = dateRange.match(
			/(\d{4}-\d{2}-\d{2})-(\d{4}-\d{2}-\d{2})/,
		) as any

		return { from, to }
	}

	const [start, end] = dateRange

	return {
		from: start,
		to: !end || end === 'today' ? RANGES.today.to(now) : end,
	}
}

function dateFormat(date: Date | string) {
	return format(new Date(date), FORMAT)
}

function dateTimeFormat(date: Date | string | number | null | undefined) {
	const parsed = new Date(date ?? '')
	if (!isValid(parsed)) {
		return ''
	}
	return format(parsed, `${FORMAT} HH:mm`)
}

export {
	dateFormat,
	dateTimeFormat,
	getLabel,
	getRange,
	getRanges,
	parseDate as parse,
	utc,
}
